import { Calendar, CircleAlert, Eye, PenLine } from 'lucide-react'
import PropTypes from 'prop-types'
import { FormModal, FormModalFooter } from '@components/branded-modal'
import { useSchedulerOrganisation, SCHEDULER_ORGANISATION_STATUS } from '@hooks/useSchedulerOrganisation'
import { SubStatusEnum } from '../../../enums/enums'
import { AppointmentDetails } from '../schedule-calendar/AppointmentDetails'
import {
  appointmentCreatedBy,
  appointmentDetailsLine,
  appointmentStatusChip,
  canEditAppointment,
  formatAppointmentWindow
} from './existingAppointment'

const CARD_BUTTON_CLASS =
  'inline-flex items-center gap-1.5 rounded-md border border-[#D1D5D8] bg-white px-3 py-1.5 text-[12px] font-medium text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
const intro = (existing, editable) => {
  if (existing.appointment) {
    return editable
      ? 'This quote already has an appointment. Open it to view or reschedule, or schedule another appointment for this quote.'
      : "This quote has a past appointment. Past appointments can't be edited. Schedule another appointment to rebook this quote."
  }
  if (existing.subStatus) {
    const label = SubStatusEnum[existing.subStatus]?.label || existing.subStatus
    return `This quote is marked ${label}. Schedule another appointment for this quote?`
  }
  return 'This quote already has an appointment. Schedule another appointment for this quote?'
}

/**
 * CAR-4034 — shown before New Appointment on a quote that is already booked (design: "Appointment Already
 * Scheduled"). An upcoming appointment offers View and Edit / Reschedule; a past one only View. CANCEL (default
 * focus) does nothing; SCHEDULE ANOTHER opens today's form. `clientMayEdit`: client users may still book.
 */
export const ExistingAppointmentDialog = ({ existing, quoteReference, onCancel, onScheduleAnother, onView, onEdit }) => {
  if (!existing) return null
  const { appointment } = existing
  const editable = Boolean(appointment) && canEditAppointment(appointment)
  const chip = appointment && appointmentStatusChip(appointment)
  const createdBy = appointment && appointmentCreatedBy(appointment)
  const quoteLabel = quoteReference ? `Quote #${quoteReference}` : 'this quote'

  // The standard pop-up footer (CAR-4050): outlined Cancel focused first, navy Schedule Another.
  const footer = <FormModalFooter onCancel={onCancel} onConfirm={onScheduleAnother} confirmLabel='Schedule Another' />

  return (
    <FormModal
      open
      onClose={onCancel}
      icon={Calendar}
      eyebrow={quoteReference ? `Quote #${quoteReference} · New Appointment` : 'New Appointment'}
      title='Appointment Already Scheduled'
      footer={footer}
      clientMayEdit
    >
      <p className='text-[13px] leading-5 text-[#334155]'>{intro(existing, editable)}</p>

      {appointment && (
        <div data-testid='existing-appointment-row' className='mt-4 rounded-xl border border-[#EBEDF0] bg-[#FAFBFC] px-4 py-3'>
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <div className='flex items-center gap-2'>
              {appointment.reference_no && <span className='font-mono text-[11px] text-[#475569]'>{appointment.reference_no}</span>}
              {chip?.label && (
                <span
                  className={
                    chip.missed
                      ? 'inline-flex items-center gap-1 rounded-full bg-[#FEE2E2] px-2 py-0.5 text-[11px] font-medium text-[#DC2626]'
                      : 'inline-flex items-center gap-1 rounded-full bg-[#DBEAFE] px-2 py-0.5 text-[11px] font-medium text-[#1F6FEB]'
                  }
                >
                  {chip.missed ? <CircleAlert className='h-3 w-3' /> : <span className='h-1.5 w-1.5 rounded-full bg-current' />}
                  {chip.label}
                </span>
              )}
            </div>
            <span className='text-[11px] text-[#475569]'>
              {editable ? createdBy && `Created by ${createdBy}` : "Past · can't be edited"}
            </span>
          </div>
          <div className='mt-2 text-[14px] font-semibold text-[#0A1628]'>{formatAppointmentWindow(appointment)}</div>
          <div className='text-[12px] text-[#475569]'>{appointmentDetailsLine(appointment)}</div>
          <div className='mt-3 flex flex-wrap gap-2'>
            <button type='button' className={CARD_BUTTON_CLASS} onClick={() => onView(appointment.id)}>
              <Eye className='h-3.5 w-3.5' />
              View Appointment
            </button>
            {editable && (
              <button type='button' className={CARD_BUTTON_CLASS} onClick={() => onEdit(appointment.id)}>
                <PenLine className='h-3.5 w-3.5' />
                Edit / Reschedule
              </button>
            )}
          </div>
        </div>
      )}

      <p className='mt-4 text-[12px] text-[#475569]'>
        {appointment && !editable
          ? `The new appointment is linked to ${quoteLabel}. The past appointment stays in its history.`
          : `Scheduling another keeps this appointment. Both stay linked to ${quoteLabel}.`}
      </p>
    </FormModal>
  )
}

ExistingAppointmentDialog.propTypes = {
  existing: PropTypes.shape({ appointment: PropTypes.object, subStatus: PropTypes.string }),
  quoteReference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCancel: PropTypes.func.isRequired,
  onScheduleAnother: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired
}

/**
 * The Scheduler's own appointment modal, opened from a quote page. The organisation store is
 * only filled on the Scheduler pages, and Edit saves with it, so wait until it is loaded.
 */
export const QuoteAppointmentDetails = ({ appointmentId, editMode, onClose }) => {
  const { status } = useSchedulerOrganisation()
  if (status === SCHEDULER_ORGANISATION_STATUS.LOADING) return null
  return (
    <AppointmentDetails
      key={`${appointmentId}-${editMode}`}
      selectedEventId={appointmentId}
      initialEditMode={editMode}
      cancel={onClose}
      setRefresh={() => {}}
    />
  )
}

QuoteAppointmentDetails.propTypes = {
  appointmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  editMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired
}
